import { mergeRefs, useObjectRef } from '@react-aria/utils';
import type { ForwardedRef, JSX } from 'react';
import { createContext, forwardRef, useContext, useMemo, useRef } from 'react';
import type {
  DialogProps as AriaDialogProps,
  PopoverProps as AriaPopoverProps,
} from 'react-aria-components';
import {
  Provider,
  composeRenderProps,
  DEFAULT_SLOT,
} from 'react-aria-components';

import { CloseIcon } from '@infinex/ui-icons';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import {
  cn,
  scrollMask,
  useIsSmallScreen,
  useMediaQuery,
} from '@infinex/ui-styles';
import { isTouchDevice } from '@infinex/ui-utils/dom';

import {
  DIALOG_VIRTUAL_KEYBOARD_OFFSET_ID,
  DialogVars,
  isSafari26,
  SheetVars,
} from './constants';
import { DialogPrimitive } from './DialogPrimitive';
import { useDialogRouteState } from './DialogRouteState';
import type { ModalPrimitiveProps } from './ModalPrimitive';
import { ModalPrimitive } from './ModalPrimitive';
import { PopoverPrimitive } from './PopoverPrimitive';
import { SheetPrimitive } from './SheetPrimitive';
import { TrayPrimitive } from './TrayPrimitive';
import { useAriaDialogState } from './useDialogState';

import type { ButtonProps } from '../button';
import { Button, ButtonGroupContext } from '../button';
import {
  CenterBaseline,
  ContentContext,
  Footer,
  FooterContext,
  Header,
  HeaderContext,
  HeadingContext,
  ImageContext,
} from '../content';

// Context
// -----------------------------------------------------------------------------

export type DialogContextValue = {
  /**
   * Whether the dialog is dismissable.
   */
  isDismissable: boolean | undefined;
  /**
   * Whether the dialog has keyboard dismiss disabled.
   */
  isKeyboardDismissDisabled: boolean | undefined;
  /**
   * The type of the current dialog.
   *
   * @remarks
   * - only available when referenced inside of a dialog's render
   * - may change depending on the screen size
   */
  type: DialogType | undefined;
};
export const DialogContext = createContext<DialogContextValue | null>(null);

export type DialogProps = Omit<
  AriaDialogProps,
  'className' | 'style' | 'role' | 'slot'
> &
  DialogTypeProps &
  Pick<
    ModalPrimitiveProps,
    | 'isDismissable'
    | 'isKeyboardDismissDisabled'
    | 'onOpenComplete'
    | 'onCloseComplete'
    | 'size'
  > &
  Pick<
    AriaPopoverProps,
    'placement' | 'triggerRef' | 'shouldCloseOnInteractOutside'
  > &
  UnsafeStyleProps;

type DialogType = 'popover' | 'modal' | 'tray' | 'sheet';

export type DialogTypeProps = {
  /**
   * The type of dialog to render.
   * @default modal
   */
  type?: Exclude<DialogType, 'tray' | 'sheet'>;
  /**
   * The type of dialog to render on mobile devices. Inherits from `type` if not provided.
   * @default modal
   */
  mobileType?: DialogType;
};

// Main
// -----------------------------------------------------------------------------

/**
 * Dialogs are windows containing contextual information, tasks, or workflows
 * that appear over the user interface.
 *
 * [Taurus docs: `Dialog`](https://ui-sb.app.infinex.dev/?path=/docs/components-dialog--docs)
 */
export const Dialog = forwardRef(function Dialog(
  props: DialogProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const {
    isDismissable,
    isKeyboardDismissDisabled,
    onCloseComplete,
    onOpenComplete,
    size = 'medium',
    type: typeProp = 'modal',
    mobileType: mobileTypeProp = typeProp,
    // Destructured out of `otherProps` so this function isn't spread onto the
    // dialog DOM element; forwarded to the popover overlay below.
    shouldCloseOnInteractOutside,
    ...otherProps
  } = props;

  const isSmallScreen = useIsSmallScreen();
  const dialogRef = useObjectRef<HTMLDivElement>(forwardedRef);
  const ariaDialogState = useAriaDialogState();
  const type = isSmallScreen ? mobileTypeProp : typeProp;
  const dialogContext = useMemo(() => {
    return { isDismissable, isKeyboardDismissDisabled, type };
  }, [isDismissable, isKeyboardDismissDisabled, type]);

  const hasDismissButton = includeDismissButton(dialogContext);

  // @ts-expect-error — "role" is supported but not exposed in consumer prop types
  const isAlertDialog = props.role === 'alertdialog';

  // Render the children multiple times inside the necessary layout
  // wrappers. Each instance hides certain children so that they are
  // all rendered in the correct locations.
  const slottedContent = composeRenderProps(props.children, (children) => (
    <DialogContext.Provider value={dialogContext}>
      <Provider
        values={[
          [
            ImageContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                feature: {
                  className: cn('w-full shrink-0 object-cover'),
                },
              },
            },
          ],
          [
            HeaderContext,
            {
              render: renderHeader,
              className: cn(
                'flex shrink-0 items-baseline',
                DialogVars.tw(
                  'mx-(--Dialog-header-gutter)',
                  'pt-(--Dialog-header-gutter)'
                ),
                {
                  'justify-center': type !== 'popover',
                  [DialogVars.tw('ps-(--Dialog-header-button-spacer)')]:
                    hasDismissButton,
                  [DialogVars.tw('px-(--Dialog-header-padding-x)')]:
                    !hasDismissButton,
                }
              ),
            },
          ],
          [
            HeadingContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                title: {
                  render: renderHeading,
                  className: cn('text-emphasis text-base font-semibold', {
                    'flex-1': hasDismissButton,
                    'text-center': type !== 'popover',
                    'text-lg': isAlertDialog,
                  }),
                },
              },
            },
          ],
          [
            ContentContext,
            {
              render: renderContent,
              className: cn('flex-1', DialogVars.tw('p-(--Dialog-padding)')),
            },
          ],
          [
            FooterContext,
            {
              className: cn(
                'flex shrink-0 items-center gap-2',
                DialogVars.tw('px-(--Dialog-padding)', 'pb-(--Dialog-padding)'),
                {
                  [SheetVars.tw('pb-(--Sheet-inset-bottom)')]: type === 'sheet',
                }
              ),
              // iOS 26 adds significant spacing above its bottom chrome; remove
              // all our bottom padding to account for it.
              // NOTE: safe-area-inset-bottom is handled by the sheet primitive
              style: {
                paddingBottom: type === 'sheet' && isSafari26() ? 0 : undefined,
              },
            },
          ],
          [
            ButtonGroupContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                actions: {
                  align: 'justify',
                  size: isAlertDialog ? 'large' : undefined,
                  render: (el) => <Footer>{el}</Footer>,
                },
              },
            },
          ],
        ]}
      >
        {children}
      </Provider>
    </DialogContext.Provider>
  ));

  const dialogElement = (
    <DialogPrimitive
      {...otherProps}
      data-dialog-type={type}
      ref={dialogRef}
      // @ts-expect-error — Intentionally omitted from prop types; should only
      // be used by the `AlertDialog` component.
      role={props.role}
    >
      {slottedContent}
    </DialogPrimitive>
  );

  const commonProps = {
    onCloseComplete,
    onOpenComplete,
    // necessary to support `DialogMarshal` usage
    isOpen: ariaDialogState.isOpen,
    onOpenChange: (isOpen: boolean) => !isOpen && ariaDialogState.close(),
  };

  if (type === 'popover') {
    return (
      <PopoverPrimitive
        placement={props.placement}
        triggerRef={props.triggerRef}
        shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
        {...commonProps}
      >
        {dialogElement}
      </PopoverPrimitive>
    );
  }

  if (type === 'sheet') {
    return (
      <SheetPrimitive
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        {...commonProps}
      >
        {dialogElement}
      </SheetPrimitive>
    );
  }

  if (type === 'tray') {
    return <TrayPrimitive {...commonProps}>{dialogElement}</TrayPrimitive>;
  }

  return (
    <ModalPrimitive
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      onOpenComplete={onOpenComplete}
      onCloseComplete={onCloseComplete}
      size={size}
    >
      {dialogElement}
    </ModalPrimitive>
  );
});

// Styled components
// -----------------------------------------------------------------------------

export function DismissButton(
  props: Omit<DialogHeaderButtonProps, 'children' | 'aria-label' | 'slot'>
) {
  return (
    <DialogHeaderButton
      aria-label="dismiss"
      slot="close"
      data-testid="dialog-dismiss-button"
      {...props}
    >
      <CloseIcon />
    </DialogHeaderButton>
  );
}

type DialogHeaderButtonProps = Omit<ButtonProps, 'prominence' | 'size'>;
export function DialogHeaderButton(props: DialogHeaderButtonProps) {
  const { className, ...otherProps } = props;

  return (
    <Button
      size="small"
      className={cn(
        'z-10 rounded-full',
        // increase hit area on touch devices
        'pointer-coarse:after:size-min-touch-target pointer-coarse:after:absolute',
        className
      )}
      {...otherProps}
    />
  );
}

// Renderers
// -----------------------------------------------------------------------------

const InsideHeaderContext = createContext(false);

function renderHeader(element: JSX.Element) {
  return <HeaderWrapper element={element} />;
}
function HeaderWrapper(props: { element: JSX.Element }) {
  const hasDismissButton = useHasDismissButton();
  if (useContext(InsideHeaderContext)) return props.element; // prevent recursion

  return (
    <InsideHeaderContext.Provider value={true}>
      <Header {...props.element.props}>
        {props.element.props.children}
        {hasDismissButton ? (
          <CenterBaseline
            key="dismiss-button"
            className={DialogVars.tw('ps-(--Dialog-header-gap)')}
          >
            <DismissButton />
          </CenterBaseline>
        ) : (
          <CenterBaseline key="dismiss-button-spacer">
            <div className={DialogVars.tw('h-(--Dialog-header-button)')} />
          </CenterBaseline>
        )}
      </Header>
    </InsideHeaderContext.Provider>
  );
}

function renderHeading(element: JSX.Element) {
  return <HeadingWrapper element={element} />;
}
function HeadingWrapper(props: { element: JSX.Element }) {
  if (useContext(InsideHeaderContext)) return props.element; // already wrapped
  return <Header>{props.element}</Header>;
}

function renderContent(element: JSX.Element) {
  return (
    <Provider
      // clear contexts, so children can compose slot components
      values={[
        [ButtonGroupContext, null],
        [ContentContext, null],
        [FooterContext, null],
        [HeaderContext, null],
        [HeadingContext, null],
        [ImageContext, null],
      ]}
    >
      <ContentWrapper element={element} />
    </Provider>
  );
}

function ContentWrapper({ element }: { element: JSX.Element }) {
  const withinRouter = !!useDialogRouteState();
  const isTallScreen = useMediaQuery('(height >= 400px)');
  const ref = useRef<HTMLDivElement>(null);

  const scrollbarGutterWidth = ref.current
    ? ref.current?.offsetWidth - ref.current?.clientWidth
    : 0;
  const scrollbarWidth = scrollbarGutterWidth
    ? `${scrollbarGutterWidth}px`
    : DialogVars.padding;

  return (
    <div
      {...element.props}
      ref={mergeRefs(ref, element.props.ref)}
      className={cn(
        'flex-1',
        DialogVars.tw('p-(--Dialog-padding)'),
        // On "short" screens, squashing the content area between the header and
        // footer can lead to an uncomfortably small scrollable area. Instead, we
        // have elements stack naturally and let the `AriaDialog` element handle
        // scroll behaviour.
        //
        // Inside the `DialogRouter`, content must scroll to keep the header
        // fixed to the top of dialog.
        { 'overflow-y-auto': isTallScreen || withinRouter },
        element.props.className
      )}
      style={{
        ...element.props.style,
        mask: scrollMask(DialogVars.padding, scrollbarWidth),
      }}
    >
      {element.props.children}

      {isTouchDevice() && (
        <div
          role="presentation"
          id={DIALOG_VIRTUAL_KEYBOARD_OFFSET_ID}
          className="ease-out-smooth h-0 shrink-0 transition-all duration-300"
          // NOTE: review this approach when browser support gets better
          // style={{ height: 'env(keyboard-inset-height, 0px)' }}
        />
      )}
    </div>
  );
}

// Helpers ---------------------------------------------------------------------

export function useHasDismissButton(): boolean {
  const dialogContext = useContext(DialogContext);
  if (!dialogContext) return false;
  return includeDismissButton(dialogContext);
}

const withCloseButton = new Set(['modal', 'sheet']);
export function includeDismissButton(props: DialogContextValue): boolean {
  const { isDismissable, type } = props;
  return Boolean(isDismissable && type && withCloseButton.has(type));
}
